import * as React from 'react';
import { useState, useEffect, createContext } from 'react';
import Dump from "../components/dump";
import Spinner from "../components/spinner";
import { type SessionData } from "../data/sessionData";
import { isEmptyObject } from "../helpers/utils";
import { fetchOffer, fetchState, goToStep } from "../loaders";
import MessageBox from "../src/msg";
import HorizontalSteps from "../src/steps";
import SummaryMobile from "../src/summaryMobile";
import Contact from "./contact";
import DirectDebit from "./directDebit";
import Membership from "./membership";
import Payment from "./payment";
import Products from "./products";
import RetryPaymentButton from "./retryPaymentButton";
import { checkoutStepIndex, checkoutStepStates, isSubscription, parseStartDate, skipsDirectDebit } from "./utils";
import { useAvailableDiscount } from "../components/discount";

type Props = {
	clubId?: string | null;
	memberRegistrationUrl?: string | null;
	memberLoginUrl?: string | null;
	passwordProfile?: string | null;
}

function getInitialHash() {
	const l = localStorage.getItem('tokenHash');
	return (l !== 'null' && l !== null) ? l : null;
}

/*
	Capture a ?plan= deep link ONCE as the module loads, then strip it from the URL. The
	link means "start this product's signup": App resets to a clean session for it below.
	Stripping it immediately (before any render) is what makes that safe to repeat — a later
	page refresh, or an in-app switch to a different plan followed by a refresh, must not
	re-apply the original param and drag the member back to the linked product.
*/
function capturePlanParam(): string | null {
	try {
		const url = new URL(globalThis.location.href);
		const plan = url.searchParams.get('plan');
		if (plan !== null) {
			url.searchParams.delete('plan');
			globalThis.history.replaceState(null, '', url.pathname + url.search + url.hash);
		}
		return plan;
	} catch (e) {
		return null;
	}
}
const PRESELECT_PLAN = capturePlanParam();

export const ContextSession: React.Context<SessionData> = createContext(null);

function isOfferLoaded(offer: any): boolean {
	return (!offer || isEmptyObject(offer));
}

/*
	Only the steps that actually render the offer wait for it.

	The outcome screens don't use it, and gating them on `step > 1` left the
	loading spinner sitting above the message — permanently, if the offer never
	arrives, which is exactly the case on a session that has finished.
 */
function needsOffer(step: number): boolean {
	return step >= 2 && step <= 6;
}

function App({clubId, memberRegistrationUrl, memberLoginUrl, passwordProfile, ...otherProps}: Props) {
	const [state, setState] = useState<SessionData>(null);
	const [offer, setOffer] = useState(null);
	// The product a ?plan= deep link asked for, passed to the plans step to auto-select.
	// Set only once the session is known not to be a committed checkout (see below).
	const [preselectPlan, setPreselectPlan] = useState<string | null>(null);

	// For the compact mobile summary shown in the header on the checkout steps.
	const mobileDiscount = useAvailableDiscount(state?.hash ?? '', isSubscription(offer));

	const stepIndex = checkoutStepIndex(state);
	const directDebit = !skipsDirectDebit(state);

	useEffect(() => {
		const existingHash = getInitialHash();

		(async () => {
			let data = await fetchState(existingHash, clubId);
			const committed = Boolean(data?.mandateId || data?.payableId);

			// A product deep link starts that product's signup on a CLEAN session, so it
			// overrides whatever half-finished checkout the browser was remembering. The one
			// exception is a committed checkout (a mandate/payment already taken), which is
			// never disturbed — we resume it and ignore the link, matching the lock-don't-edit
			// rule the rest of the flow follows. (A fresh first visit already has no session,
			// so there is nothing to discard.)
			if (PRESELECT_PLAN && !committed && existingHash) {
				data = await fetchState('', clubId);
			}

			if (data) {
				localStorage.setItem('tokenHash', data?.hash ? String(data.hash) : null);
				setState(data);
				if (PRESELECT_PLAN && !committed) {
					setPreselectPlan(PRESELECT_PLAN);
				}
			}
		})();
	}, []);

	useEffect(() => {
		if (state?.hash) {
			fetchOffer(state.hash).then((data) => {
				if (data) {
					setOffer(data);
				}
			});
		}
	}, [state?.offerId]);

	return (<>
		<ContextSession.Provider value={state}>

			<div className="max-w-screen-xl mx-auto px-6 sm:px-8">
				<div className="border-b border-white py-3 sm:py-6 md:py-8 mb-8 flex justify-between lg:justify-center gap-4">
					<img src="/images/logo/logo.svg" alt="Feelfit"/>

					{(state?.step < 2 || isOfferLoaded(offer)) ?
						null :
						<div className="block lg:hidden">
							<SummaryMobile offer={offer} startDate={parseStartDate(state?.startDate) ?? new Date()} discount={mobileDiscount}/>
						</div>
					}
				</div>

				{/*
					Hidden on the plan list and the outcome screens. The labels depend
					on the path: a one-off product has no direct debit step.
				*/}
				{stepIndex !== null && <HorizontalSteps
					states={checkoutStepStates(state)}
					onStepSelect={(targetStep) => goToStep(state.hash, targetStep, setState)}
				/>}
			</div>

			{state === null ? <div className="text-center my-16"><Spinner/></div> : <>
				{state.step == 1 && <Products setState={setState} preselectPlan={preselectPlan}/>}

				{(needsOffer(state.step) && isOfferLoaded(offer)) ?
					<div className="text-center my-16"><Spinner/></div> : <>
						{state.step == 2 && <Membership setState={setState} offer={offer} memberLoginUrl={memberLoginUrl}/>}
						{state.step == 3 && <Contact setState={setState} offer={offer} memberRegistrationUrl={memberRegistrationUrl} memberLoginUrl={memberLoginUrl} passwordProfile={passwordProfile}/>}
						{(state.step == 4 || state.step == 5) && <DirectDebit setState={setState} offer={offer} memberLoginUrl={memberLoginUrl}/>}
						{state.step == 6 && <Payment setState={setState} offer={offer}/>}
					</>
				}

				{/*
					Step 8 and step 9 must not behave the same way.

					Step 9 is a known failure — the card was declined and no money moved,
					so offering another attempt is safe and worth doing: everything else
					the customer filled in still stands.

					Step 8 is an unconfirmed payment. They may well have been charged, so
					there is deliberately no way to pay again from here.
				*/}
				{state.step == 8 && <MessageBox name="CONFIRMING YOUR PAYMENT">
					<h3>THIS USUALLY TAKES A FEW MOMENTS</h3>
					<p className="mb-4">
						We’re waiting for your bank to confirm the payment. Your details{directDebit && <> and your
						Direct Debit</>} have been saved.
					</p>
					<p className="mb-8">
						<strong>Please don’t pay again.</strong> If the payment went through, you’ll
						get an email confirming your membership. If you haven’t heard anything within
						an hour, contact the club and quote your email address — they’ll be able to
						see exactly what happened.
					</p>
					<a href="/" className="button !mb-0 !flex items-center justify-center">RETURN TO HOMEPAGE</a>
				</MessageBox>}

				{state.step == 9 && <MessageBox name="PAYMENT UNSUCCESSFUL">
					<h3>NO PAYMENT HAS BEEN TAKEN</h3>
					<p className="mb-4">
						Your bank declined the payment, so nothing has been charged. This is usually a
						card detail typed incorrectly, or a limit on the card.
					</p>
					<p className="mb-8">
						Everything else you’ve entered is saved{directDebit && <>, including your Direct Debit</>} — you only
						need to pay again to finish joining.
					</p>
					<RetryPaymentButton setState={setState}/>
					<a href="/" className="button grey !mb-0 !flex items-center justify-center">RETURN TO HOMEPAGE</a>
				</MessageBox>}

				{state.step === 10 && <MessageBox name="THANK YOU">
					<h3>YOUR MEMBERSHIP IS BEING PROCESSED</h3>
					<p className="mb-8">
						Welcome to Feel Fit Gym! We’ll email you confirmation of your membership shortly.
						You can sign in to your account below to manage your membership and book classes.
					</p>
					<a href={memberLoginUrl || '/'} className="button !mb-0 !flex items-center justify-center">SIGN IN TO YOUR ACCOUNT</a>
				</MessageBox>}

				{/*<Dump value={state?.widgetUrl} label="widgetUrl"/>*/}
				{/*<Dump value={state?.step} label="step"/>*/}
				{/*<Dump value={state?.offerId} label="offerId"/>*/}
				{/*<Dump value={state?.startDate} label="startDate"/>*/}
				{/*<Dump value={state?.contactId} label="contactId"/>*/}
				{/*<Dump value={state?.saleId} label="saleId"/>*/}
				{/*<Dump value={state?.mandateId} label="mandateId"/>*/}
				{/*<Dump value={state?.payableId} label="payableId"/>*/}
				{/*<Dump value={countries}/>*/}
				{/*<Dump value={offer} label="Offer"/>*/}
			</>}

		</ContextSession.Provider>
	</>);
}

export default App;
